import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, AD_KIND_LABEL, type AdKind, type Mark, type MarkAd, type QidUser } from '../../api';
import { MarkList, type TilePrice } from './MarkList';
import { marketplaceAds } from './marketplace';
import { importInfo } from './markData';
import { datesText } from '../../mark-tabs/AdsTab';

export const adPath = (ad: Pick<MarkAd, 'id'>) => `/annons/${ad.id}`;
export function adIdFromPath(path: string): string | null {
  const m = path.match(/^\/annons\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

/** Annonsens pris på kortet, t.ex. "9 000 kr / år", "från 2 500 kr / dag", "18 000 kr / paket". */
export function adTilePrice(ad: MarkAd): TilePrice {
  if (!(ad.priceAmount > 0)) return null;
  const amount = `${Math.round(ad.priceAmount).toLocaleString('sv-SE')} kr`;
  return { price: ad.kind === 'enskild' ? `från ${amount}` : amount, unit: `/ ${ad.priceUnit}`, sort: ad.priceAmount };
}

const NOUN: Record<AdKind, string> = { jaktlag: 'jaktlag', jaktklubb: 'jaktklubbar', enskild: 'enskilda jakter', jaktpaket: 'jaktpaket' };

/**
 * Marknadens lista för en annonstyp. Varje annons visas med sin marks bild, viltförekomst och
 * läge men med annonsens rubrik, pris och datum.
 */
export function AdList({ kind, user, onOpen, onLogin, reloadKey, inArea }: {
  kind: AdKind; user?: QidUser | null; onOpen: (ad: MarkAd) => void; onLogin?: () => void; reloadKey?: unknown;
  /** Områdesfilter från sökfältets kartnål. */ inArea?: (lat: number | null | undefined, lng: number | null | undefined) => boolean;
}) {
  const [ads, setAds] = useState<MarkAd[] | null>(null);
  useEffect(() => { setAds(null); api.publishedAds(kind).then(setAds).catch(() => setAds([])); }, [kind, reloadKey]);

  const byId = useMemo(() => new Map((ads ?? []).map(a => [a.id, a])), [ads]);
  // Posten i listan är annonsens mark med annonsens id och rubrik, så att filter och karta fungerar som för marker.
  const entries = useMemo<Mark[]>(() => marketplaceAds(ads ?? []).filter(a => !inArea || inArea(a.mark!.mapLat, a.mark!.mapLng)).map(a => ({ ...a.mark!, id: a.id, name: a.title, ...(a.imageUrl ? { imageUrl: a.imageUrl } : {}) })), [ads, inArea]);

  const priceOf = useCallback((m: Mark) => { const a = byId.get(m.id); return a ? adTilePrice(a) : null; }, [byId]);
  const kindOf = useCallback((m: Mark) => {
    const a = byId.get(m.id);
    if (!a) return AD_KIND_LABEL[kind];
    if (a.kind === 'jaktklubb' && a.memberCount) return `${a.memberCount} medlemmar`;
    if (a.kind === 'jaktpaket') return `${a.lines.length} delar`;
    return a.markName;
  }, [byId, kind]);
  const subOf = useCallback((m: Mark) => {
    const a = byId.get(m.id);
    if (!a) return undefined;
    if (a.kind === 'enskild') return `${datesText(a.dates)}${a.spots ? ` · ${a.spots} platser` : ''}`;
    if (a.kind === 'jaktpaket') return a.lines.map(l => (l.kind === 'jaktklubb' ? `Jaktklubb ${l.markName}` : `${l.days} dagar ${l.markName}`)).join(' + ');
    const imp = importInfo(m);
    return imp ? `Importerad från ${imp.label}` : undefined;
  }, [byId]);

  return (
    <MarkList
      marks={entries} loading={ads === null} user={user} kind={AD_KIND_LABEL[kind]} noun={NOUN[kind]}
      onOpen={m => { const a = byId.get(m.id); if (a) onOpen(a); }} onLogin={onLogin}
      emptySub={inArea ? 'Inga i det valda området' : `Inga publicerade ${NOUN[kind]} just nu`}
      priceOf={priceOf} kindOf={kindOf} subOf={subOf} hrefOf={m => adPath({ id: m.id })}
    />
  );
}
